import type { ReactNode } from 'react';
import { useLocation } from 'react-router';
import { ErrorBoundary } from 'react-error-boundary';

import { isChunkLoadError, reloadForStaleChunk } from '@/lib/chunk-load-error';
import ErrorPage from '@/pages/error';
import { useCompanyStore } from '@/stores/company.store';

/**
 * The error page around a layout's routed screen.
 *
 * Reset on the route and on the tenant scope. The boundary sits outside the
 * outlet, so without reset keys one caught error kept the error page up on
 * every page after it, through navigation and tenant switches alike, until a
 * full reload.
 *
 * A stale chunk after a deploy is not an error in the page: it reloads onto
 * the current build instead (see `chunk-load-error.ts`).
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const scopeEpoch = useCompanyStore(state => state.scopeEpoch);

  return (
    <ErrorBoundary
      fallback={<ErrorPage />}
      resetKeys={[pathname, scopeEpoch]}
      onError={error => {
        if (isChunkLoadError(error)) reloadForStaleChunk();
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
